import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail,
  Lock,
  ArrowLeft,
  LogIn,
  Eye,
  EyeOff,
  AlertCircle,
  Shield,
  TrendingUp,
  Globe,
  Zap,
  ArrowRight,
  Award,
  ChevronRight,
  Fingerprint,
  Loader2,
} from "lucide-react";
import { useDispatch } from "react-redux";
import { userLogin } from "../../store/slices/authSlice";
import { useToast } from "../../components/common/ThemeToaster";

const UserLogin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    success: toastSuccess,
    error: toastError,
    info: toastInfo,
  } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [touched, setTouched] = useState({
    email: false,
    password: false,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setFocusedField(null);
  };

  const handleFocus = (field) => {
    setFocusedField(field);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const getFieldError = (field) => {
    if (!touched[field]) return null;

    switch (field) {
      case "email":
        if (!formData.email) return "Email is required";
        if (!validateEmail(formData.email))
          return "Please enter a valid email address";
        return null;
      case "password":
        if (!formData.password) return "Password is required";
        if (formData.password.length < 6)
          return "Password must be at least 6 characters";
        return null;
      default:
        return null;
    }
  };

  const inputClasses = (field) => {
    const hasError = getFieldError(field);
    const isFocused = focusedField === field;

    return `w-full px-4 py-3.5 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border rounded-xl transition-all duration-200
      ${
        hasError
          ? "border-red-500 focus:ring-red-500"
          : isFocused
            ? "border-primary-500 ring-2 ring-primary-500/20"
            : "border-gray-200/50 dark:border-gray-700/50 focus:border-primary-500"
      } focus:outline-none focus:ring-2 transition-all text-gray-900 dark:text-white placeholder-gray-400`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({
      email: true,
      password: true,
    });

    // Validation
    if (!validateEmail(formData.email)) {
      toastError("Please enter a valid email address");
      return;
    }
    if (!formData.password) {
      toastError("Password is required");
      return;
    }
    if (formData.password.length < 6) {
      toastError("Password must be at least 6 characters");
      return;
    }

    setIsLoading(true);

    try {
      const result = await dispatch(
        userLogin({
          email: formData.email.trim(),
          password: formData.password,
        }),
      ).unwrap();

      toastSuccess("Login successful! Redirecting to your dashboard...");
      toastInfo(`Welcome back, ${result.user?.firstName || "User"}!`);

      setTimeout(() => {
        navigate("/user/profile");
      }, 1500);
    } catch (err) {
      console.error("Login error:", err);

      let errorMessage = "Login failed. Please check your credentials.";

      if (typeof err === "string") {
        errorMessage = err;
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      toastError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    { icon: TrendingUp, text: "Access to premium suppliers" },
    { icon: Globe, text: "Global trade network" },
    { icon: Shield, text: "Secure transactions" },
    { icon: Zap, text: "Fast response times" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[40%] -left-[20%] w-[80%] h-[80%] bg-gradient-to-br from-primary-200/20 to-secondary-200/20 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-[40%] -right-[20%] w-[80%] h-[80%] bg-gradient-to-tl from-primary-300/10 to-secondary-300/10 dark:from-primary-800/10 dark:to-secondary-800/10 rounded-full blur-3xl animate-pulse delay-1000" />

        {/* Floating particles */}
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1.5 h-1.5 bg-primary-400/30 rounded-full animate-float"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`,
            }}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-6xl w-full relative z-10"
      >
        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          onClick={() => navigate("/")}
          className="group flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-primary-600 transition-colors mb-6"
        >
          <div className="p-1.5 rounded-xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 group-hover:bg-primary-50 dark:group-hover:bg-primary-900/20 transition-colors">
            <ArrowLeft
              size={16}
              className="group-hover:text-primary-600 transition-colors"
            />
          </div>
          <span>Back to Home</span>
        </motion.button>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left Side - Welcome Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="hidden lg:block"
          >
            <div className="bg-gradient-to-br from-primary-600 via-primary-500 to-secondary-600 rounded-3xl p-8 h-full relative overflow-hidden shadow-2xl">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />

              <div className="relative z-10">
                {/* Logo Badge */}
                <div className="mb-8">
                  <div className="inline-flex items-center gap-3 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full border border-white/30 mb-6">
                    <img
                      src="/Icon.png"
                      alt="TradeBridge Logo"
                      className="w-5 h-5 object-contain"
                    />
                    <span className="text-xs font-bold text-white uppercase tracking-wider">
                      TradeBridge
                    </span>
                  </div>
                  <h2 className="text-3xl font-bold text-white mb-4">
                    Sign in to Your Account
                  </h2>
                  <p className="text-white/90 leading-relaxed">
                    Access your dashboard, manage your requests, and connect
                    with global suppliers.
                  </p>
                </div>

                {/* Features List */}
                <div className="space-y-4 mb-8">
                  {features.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                      className="flex items-center gap-3 text-white/90"
                    >
                      <div className="p-1.5 bg-white/20 rounded-lg">
                        <feature.icon size={16} className="text-primary-600" />
                      </div>
                      <span className="text-sm">{feature.text}</span>
                    </motion.div>
                  ))}
                </div>

                {/* Trust Badge */}
                <div className="mt-6 pt-6 border-t border-white/20">
                  <div className="flex items-center justify-center gap-2 text-white/80 text-xs">
                    <Award size={14} />
                    <span>Trusted by hundreds businesses worldwide</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Side - Login Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-800/50 overflow-hidden"
          >
            {/* Header */}
            <div className="relative p-6 sm:p-8 bg-gradient-to-r from-primary-600/5 to-secondary-600/5 border-b border-gray-100 dark:border-gray-800">
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 dark:bg-primary-900/20 rounded-full mb-4">
                  <LogIn size={14} className="text-primary-600" />
                  <span className="text-xs font-medium text-primary-600">
                    Sign In
                  </span>
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  Welcome Back
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  Sign in to continue to your account
                </p>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 sm:p-8">
              {/* Email Field */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  {/* Icon wrapper */}
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
                    <Mail size={18} className="text-gray-400" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onFocus={() => handleFocus("email")}
                    onBlur={() => handleBlur("email")}
                    className={`${inputClasses("email")} pl-10`}
                    placeholder="john@company.com"
                    autoComplete="email"
                    style={{ position: "relative", zIndex: 1 }}
                  />
                </div>
                {getFieldError("email") && (
                  <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle size={12} />
                    {getFieldError("email")}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
                    <Lock size={18} className="text-gray-400" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onFocus={() => handleFocus("password")}
                    onBlur={() => handleBlur("password")}
                    className={`${inputClasses("password")} pl-10 pr-10`}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    style={{ position: "relative", zIndex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors z-10"
                    style={{ background: "transparent" }}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {getFieldError("password") && (
                  <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle size={12} />
                    {getFieldError("password")}
                  </p>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between mb-8">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Remember me
                  </span>
                </label>
                <Link
                  to="/user/forgot-password"
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium hover:underline transition-colors"
                >
                  Forgot password?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-4 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-all active:scale-[0.98] flex items-center justify-center gap-3 shadow-lg shadow-primary-500/20"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <Fingerprint size={20} />
                    <span>Sign In</span>
                    <ArrowRight size={18} />
                  </>
                )}
              </button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200 dark:border-gray-700" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white dark:bg-gray-900 text-gray-500">
                    New to TradeBridge?
                  </span>
                </div>
              </div>

              {/* Sign Up Link */}
              <div className="text-center">
                <Link
                  to="/user/register"
                  className="inline-flex items-center gap-2 text-primary-600 font-medium hover:gap-3 transition-all"
                >
                  Create an account
                  <ChevronRight size={16} />
                </Link>
              </div>

              {/* Security Note */}
              <div className="mt-6 text-center">
                <div className="inline-flex items-center gap-2 text-xs text-gray-500">
                  <Shield size={12} />
                  <span>
                    Your data is protected with enterprise-grade security
                  </span>
                </div>
              </div>
            </form>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default UserLogin;
